import cx from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';

import CurrentUser from 'auth/CurrentUser';
import DividerText from 'components/DividerText.jsx';
import Group from 'entities/Group';
import LayerTrigger from 'components/LayerTrigger.jsx';
import linkStateWithStores from 'components/mixins/linkStateWithStores';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import MessageActions from 'groups/actions/MessageActions';
import MessageStore from 'groups/stores/MessageStore';
import { Roles } from 'user/UserConstants';
import ScrollWatch from 'components/ScrollWatch.jsx';
import { markChange } from 'lib/Timer';
import Tooltip from 'components/Tooltip.jsx';

import GroupMessage from './GroupMessage.jsx';
import GroupMessagesNull from './GroupMessagesNull.jsx';
import GroupScheduledMessageList from './GroupScheduledMessageList.jsx';

import './styles/GroupMessageList.scss';

const GroupMessageList = React.createClass({
  propTypes: {
    group: React.PropTypes.instanceOf(Group).isRequired,
  },

  contextTypes: {
    router: React.PropTypes.object.isRequired,
  },

  mixins: [
    linkStateWithStores([MessageStore]),
  ],

  getInitialState() {
    const lastReadSeq = this.props.group.getLastReadSeq();
    return Object.assign(this.getStateFromStores(), {
      width: 0,
      lastReadSeq,
      initialFirstMessage: null,
      newMessagesSeen: false,
      visibility: null,
    });
  },

  componentDidMount() {
    window.addEventListener('resize', this.onResize, false);
    this.onResize();
  },

  componentWillUpdate(nextProps, nextState) {
    const { group } = nextProps;
    const messages = nextState.messages;
    // New announcement line gets reset if the user makes an announcement
    if (this.needsNewLineReset(messages)) {
      this.refs.aboveTrigger.hideLayer();
      this.refs.belowTrigger.hideLayer();
      this.setState({ lastReadSeq: messages.first().getSeq() });
    }

    // Reset new announcement line visibility for additional incoming messages
    if (this.state.newMessagesSeen && this.isFirstMessageNotRead(messages, group)) {
      this.setState({ newMessagesSeen: false }, () => this.onVisibilityChange(this.state.visibility));
    }

    if (this.props.group.id !== group.id) {
      this.setState(this.getStateFromStores(group.id));
    }
  },

  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading === true && this.state.loading === false) {
      markChange('class_open', 'loaded');
    }
  },

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize, false);
  },

  onResize() {
    const width = ReactDOM.findDOMNode(this.refs.groupMessageList).offsetWidth;

    if (this.state.width !== width) {
      this.setState({ width });
    }
  },

  onVisibilityChange(visibility) {
    // Keep track of visibility for when an update occurs
    if (visibility !== this.state.visibility) {
      this.setState({ visibility });
    }

    if (this.state.newMessagesSeen) {
      return;
    }

    switch (visibility) {
      case 'above':
        this.refs.aboveTrigger.showLayer();
        break;
      case 'below':
        this.refs.belowTrigger.showLayer();
        break;
      default:
        this.refs.aboveTrigger.hideLayer();
        this.refs.belowTrigger.hideLayer();

        this.setState({ newMessagesSeen: true });
        break;
    }
  },

  getStateFromStores(groupId = this.props.group.id) {
    const messages = MessageStore.getMessages(groupId);
    return {
      gap: MessageStore.getGap(groupId),
      loading: !MessageStore.isDoneLoadingForGroup(groupId),
      messagesScheduled: MessageStore.getMessagesScheduled(groupId),
      initialFirstMessage: messages.first(),
      messages,
    };
  },

  getChattableOwners() {
    const { group } = this.props;
    return group.getOwners().filter(o => o.getCanChat());
  },

  isFirstMessageNotRead(messages, group) {
    return (
      messages.first() &&
      messages.first().getSeq() > group.getLastReadSeq()
    );
  },

  loadMoreMessages() {
    if (this.state.gap) {
      const gapParams = this.state.gap.getGapParams();
      MessageActions.loadMessages(
        this.props.group.id,
        gapParams['max_seq'],
        gapParams['since_seq']);
    }
  },

  needsAnimationFrame() {
    return true;
  },

  needsNewLineReset(messages) {
    const { initialFirstMessage } = this.state;
    const currentFirstMessage = messages.first();
    /*
        New announcement line needs to be reset to the top if:
          The first message in the list is by the current user
          The first message is not the same first message as when the user initially enters the view
          this.state.lastReadSeq has not already been set to be the newest message
        All these cases will only be true if the new (first) message is made by the current user,
        and is currently in the view in which the message was made in.
    */
    return (
      initialFirstMessage &&
      currentFirstMessage &&
      currentFirstMessage.sentByCurrentUser() &&
      initialFirstMessage.getSeq() !== currentFirstMessage.getSeq() &&
      currentFirstMessage.getSeq() > this.state.lastReadSeq
    );
  },

  renderLoadingIndicator() {
    if (this.state.loading || this.state.gap) {
      return (
        <ScrollWatch onEnter={this.loadMoreMessages} threshold={0.5}>
          <div className="GroupMessageList-loading">
            <LoadingIndicator size="large" />
          </div>
        </ScrollWatch>
      );
    } else {
      return null;
    }
  },

  renderMessage(message) {
    const { group } = this.props;
    return (
      <GroupMessage
        key={message.getUuid()}
        message={message}
        groupId={group.id}
        groupIsOwnedByUser={group.isOwnedByUser(CurrentUser.getUserId())}
      />
    );
  },

  renderMessages() {
    if (this.state.messages.isEmpty()) {
      if (!this.state.loading && this.state.messagesScheduled.isEmpty()) {
        const { group } = this.props;
        const user = CurrentUser.getUser();
        return (
          <GroupMessagesNull
            canChat={user.canChat() && this.getChattableOwners().length > 0}
            canPost={group.canPost()}
            canInviteTeachers={user.getRole() === Roles.teacher}
            messageHistoryEnabled={group.isMessageHistoryEnabled()}
          />
        );
      }
      return null;
    }
    let messages;
    let newMessages = [];
    let oldMessages = [];
    if (this.state.lastReadSeq && !this.needsNewLineReset(this.state.messages)) {
      newMessages = this.state.messages
        .filter(message => message.getSeq() > this.state.lastReadSeq)
        .map(this.renderMessage);

      oldMessages = this.state.messages
        .filter(message => message.getSeq() <= this.state.lastReadSeq)
        .map(this.renderMessage);

      if (oldMessages.length === 0) {
        messages = newMessages;
      } else if (newMessages.length > 0) {
        newMessages.push(this.renderNewMessagesDivider());
        messages = newMessages.concat(oldMessages);
      } else {
        messages = oldMessages;
      }
    } else {
      messages = this.state.messages.map(this.renderMessage);
    }

    return messages;
  },

  renderNewMessagesDivider() {
    return (
      <ScrollWatch onVisibilityChange={this.onVisibilityChange} key={this.props.group.id}>
        <DividerText
          className="GroupMessageList-newMessagesDivider"
          text="New announcements"
          color="violet"
          weight="thick"
          uppercase={false}
          dividerPosition="bottom"
        />
      </ScrollWatch>
    );
  },

  renderScheduledMessages() {
    if (this.state.messagesScheduled.isEmpty()) {
      return null;
    }

    return (
      <GroupScheduledMessageList
        group={this.props.group}
        messages={this.state.messagesScheduled}
        collapsible
      />
    );
  },

  renderTooltipLauncher(refName, placement) {
    const tooltip = (
      <Tooltip
        hideOnEscape={false}
        hideOnBlur={false}
        hideOnTransition={false}>
        New announcements
      </Tooltip>
    );

    return (
      <LayerTrigger
        ref={refName}
        layer={tooltip}
        trigger="manual"
        placement={placement}
        alignment="middle">
        <div style={{ width: this.state.width }} />
      </LayerTrigger>
    );
  },

  render() {
    const above = this.renderTooltipLauncher('aboveTrigger', 'bottom');
    const below = this.renderTooltipLauncher('belowTrigger', 'top');

    return (
      <div
        className={cx('GroupMessageList-ClassNavExperiment', this.props.className)}
        ref="groupMessageList">
        {above}
        {this.renderScheduledMessages()}
        {this.renderMessages()}
        {this.renderLoadingIndicator()}
        {below}
      </div>
    );
  },
});

export default GroupMessageList;



// WEBPACK FOOTER //
// ./src/groups/components/GroupMessageList.jsx